import { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from '@radix-ui/themes';
import { AdMob } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';
import { clearCacheMap } from 'api';
import { adAndroidRewarded, adIosRewarded } from 'helpers/InitAdmob';
import { isCurrentUserAdmin, isCurrentUserPremium, userIsFitPlayerProfile } from 'helpers/authentication';
import { pushEvent } from 'helpers/crmUtils';
import Analytics from 'ui/design-base/Analytics';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchVisitorByUser, fetchVisitsByUser, clearVisits } from 'store/visitors/visitorsSlice';
import FollowNetworkBox from './FollowNetworkBox';
import ModalNoCard from './ModalNoCard';

const VisitNumber = ({ views = -1, playerId }: { views?: number; playerId: number }) => {
  const visitorsState = useAppSelector((state) => state.visitors);
  const { loading, clear, visitors } = visitorsState;
  views = views !== -1 ? views : (visitorsState.views ?? -1);
  const dispatch = useAppDispatch();
  const [modalNoCardOpen, setModalNoCardOpen] = useState(false);

  useEffect(
    () => () => {
      if (clear) {
        clearCacheMap();
        dispatch(clearVisits());
      }
    },
    [clear, dispatch],
  );

  const showVisitModal = useCallback(() => {
    if (isCurrentUserAdmin()) {
      dispatch(fetchVisitorByUser(playerId));
    }
  }, [dispatch, playerId]);

  const callVisitEndpoint = useCallback(async () => {
    if (userIsFitPlayerProfile()) {
      if (!isCurrentUserPremium() && Capacitor.isNativePlatform()) {
        try {
          await AdMob.prepareRewardVideoAd({
            adId: Capacitor.getPlatform() === 'ios' ? adIosRewarded : adAndroidRewarded,
            isTesting: false,
          });
        } catch (error) {
          console.error('Error preparing rewarded ad:', error);
        }
      }
      await Analytics.sendEvent({
        category: 'Profile Interaction',
        action: 'Show Visit Number',
        label: 'Player Profile',
      });
      pushEvent('TT - EVENT - visit number loaded');
      dispatch(fetchVisitsByUser(playerId));
    } else {
      await Analytics.sendEvent({
        category: 'Profile Interaction',
        action: 'Add Cart for Visit Number',
        label: 'Player Profile',
      });
      pushEvent('TT - EVENT - visit number no card modal opened');
      setModalNoCardOpen(true);
    }
  }, [dispatch, playerId]);

  const viewsNotCalledContent = useMemo(
    () =>
      loading ? (
        <Spinner />
      ) : (
        <>
          <div
            className='text-accent-accent-12 hover:bg-limea-2 text-sm leading-tight font-medium underline underline-offset-4 hover:cursor-pointer'
            onClick={callVisitEndpoint}
          >
            Scopri il numero di visite
          </div>
          <ModalNoCard
            text='Per poter vedere il numero di visite'
            open={modalNoCardOpen}
            setOpen={setModalNoCardOpen}
          />
        </>
      ),
    [loading, callVisitEndpoint, modalNoCardOpen],
  );

  const viewCalledContent = useMemo(
    () => (
      <>
        <div
          className={`visit-number w-full flex-initial text-nowrap ${isCurrentUserAdmin() ? 'underlined' : ''}`}
          onClick={showVisitModal}
        >
          NUMERO DI VISITE:
          <span className='ml-1'>
            <b>{views}</b>

            {isCurrentUserAdmin() && <FollowNetworkBox followers={visitors} followersDefault={true} />}
          </span>
        </div>
      </>
    ),
    [views, showVisitModal, visitors],
  );

  return views > 0 ? viewCalledContent : views === -1 ? viewsNotCalledContent : <></>;
};

export default VisitNumber;
